// apps/app/src/coachmark/Coachmark.tsx
// Presentational popover. Position is computed by CoachmarkHost from the
// target data-attribute's bounding rect, or docked bottom-right of the map
// area (clear of the right rail + minimap, above the mobile HUD toggles).
// Theme tokens mirror TileTooltip (apps/app/src/ui/TileTooltip.tsx) so the
// visual style is consistent.

import { Button } from '@aod/ui/components/ui/button';
import { appDom } from '../lib/appDom.js';
import { useAutoDismiss } from '../lib/useAutoDismiss.js';

interface CoachmarkProps {
  /** Hint id — drives the card's DOM id / testid (`coachmark-<hintId>`). */
  readonly hintId?: string;
  readonly title: string;
  readonly body: string;
  readonly learnMoreUrl?: string;
  readonly position: { left: number; top: number } | { dock: 'bottom-right' };
  /** Called for ✕, "Got it" and the ~10 s auto-fade alike: all three mean "seen". */
  readonly onDismiss: () => void;
  /** False pins the card until the player closes it (default: fades after ~10 s). */
  readonly autoDismiss?: boolean;
}

const DOCKED_BOTTOM_RIGHT = 'right-2 bottom-28 md:right-(--centre-inset) md:bottom-16';

export function Coachmark({
  hintId,
  title,
  body,
  learnMoreUrl,
  position,
  onDismiss,
  autoDismiss = true,
}: CoachmarkProps) {
  // Hover or keyboard focus on the card pauses the clock.
  const { phase, fadeMs, hold } = useAutoDismiss(onDismiss, { enabled: autoDismiss });
  const docked = 'dock' in position;
  const domId = `coachmark-${hintId ?? 'hint'}`;
  return (
    // biome-ignore lint/a11y/useSemanticElements: tests pin role="dialog" (task constraint) — native <dialog> would require `open` and ships conflicting UA styles for absolute positioning.
    <div
      role="dialog"
      aria-label={title}
      id={domId}
      data-testid={domId}
      {...appDom({ id: domId, type: 'Popover', scope: 'dialog', label: title })}
      data-coachmark-dock={docked ? position.dock : undefined}
      data-fade-phase={phase}
      style={{
        ...(docked ? {} : { left: position.left, top: position.top }),
        opacity: phase === 'fading' ? 0 : 1,
        transition: fadeMs > 0 ? `opacity ${fadeMs}ms ease-out` : undefined,
      }}
      {...hold}
      className={`pointer-events-auto fixed z-[300] max-w-[min(320px,calc(100vw-16px))] ${docked ? DOCKED_BOTTOM_RIGHT : ''} rounded-md border border-border/70 bg-popover/95 px-3 py-2.5 text-popover-foreground shadow-lg backdrop-blur-sm`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm uppercase tracking-[0.08em]">{title}</div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          data-testid="coachmark-close"
          {...appDom({
            id: 'coachmark-close',
            type: 'IconButton',
            role: 'button',
            scope: 'dialog',
          })}
          className="-mt-1 -mr-1 inline-flex min-h-6 min-w-6 cursor-pointer items-center justify-center px-1 text-muted-foreground hover:text-foreground"
        >
          ✕
        </button>
      </div>
      <div className="mt-1 text-xs leading-snug text-popover-foreground/90">{body}</div>
      <div className="mt-2 flex items-center justify-end gap-2">
        {learnMoreUrl && (
          <a
            href={learnMoreUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-blue-700 underline dark:text-blue-400 underline-offset-2"
          >
            Learn more
          </a>
        )}
        <Button
          type="button"
          size="sm"
          onClick={onDismiss}
          data-testid="coachmark-ack"
          {...appDom({ id: 'coachmark-ack', type: 'Button', role: 'button', scope: 'dialog' })}
          className="h-auto rounded px-2.5 py-1 text-[11px]"
        >
          Got it
        </Button>
      </div>
    </div>
  );
}
